import { useMemo } from 'react'

import * as Highcharts from 'highcharts'
import HC_heatmap from 'highcharts/modules/heatmap'
import HighchartsReact from 'highcharts-react-official'
import { useTranslation } from 'react-i18next'

import { useAnalyticsStore } from '@/store/analytics.store'
import { useThemeStore } from '@/store/theme.store'

// Initialize the heatmap module
if (typeof Highcharts === 'object') {
  if (typeof HC_heatmap === 'function') {
    (HC_heatmap as any)(Highcharts)
  } else if (HC_heatmap && typeof (HC_heatmap as any).default === 'function') {
    (HC_heatmap as any).default(Highcharts)
  }
}

export function AnalyticsHeatmap() {
  const { t } = useTranslation()
  const { reportItems, filters, setSelectedDate } = useAnalyticsStore()
  const isDark = useThemeStore(s => s.dark)

  const WEEKDAYS = useMemo(() => [
    t('analytics.weekdays.mon', 'Mon'),
    t('analytics.weekdays.tue', 'Tue'),
    t('analytics.weekdays.wed', 'Wed'),
    t('analytics.weekdays.thu', 'Thu'),
    t('analytics.weekdays.fri', 'Fri'),
    t('analytics.weekdays.sat', 'Sat'),
    t('analytics.weekdays.sun', 'Sun'),
  ], [t])

  const options = useMemo<Highcharts.Options>(() => {
    if (!filters.from || !filters.to) return {}

    const startDate = new Date(filters.from)
    const endDate = new Date(filters.to)

    // Normalize to midnight UTC to avoid timezone shifts
    startDate.setUTCHours(0, 0, 0, 0)
    endDate.setUTCHours(0, 0, 0, 0)

    const firstWeekday = (startDate.getUTCDay() + 6) % 7 // Monday = 0, Sunday = 6
    const totalDays = Math.round((endDate.getTime() - startDate.getTime()) / 86400000) + 1

    // Map data by date string YYYY-MM-DD
    const dataMap = new Map<string, number>()
    reportItems.forEach(item => {
      const dateStr = item.date.split('T')[0]
      const current = dataMap.get(dateStr) || 0
      dataMap.set(dateStr, current + (item.amountPLN || 0))
    })

    const chartData = []
    let maxVal = 0

    const weekMonths: string[] = []
    let currentMonth = -1

    for (let dayOffset = 0; dayOffset < totalDays; dayOffset++) {
      const currentDate = new Date(startDate.getTime() + dayOffset * 86400000)
      const dateStr = currentDate.toISOString().split('T')[0]

      const val = dataMap.get(dateStr) || 0
      if (val > maxVal) maxVal = val

      const xCoord = Math.floor((firstWeekday + dayOffset) / 7)
      const yCoord = (currentDate.getUTCDay() + 6) % 7 // Monday = 0, Sunday = 6

      const month = currentDate.getUTCMonth()
      if (month !== currentMonth) {
        currentMonth = month
        // Use standard JS localization for month name
        const monthStr = currentDate.toLocaleString('ru-RU', { month: 'short' })
        // Only set it if we haven't already set a month for this column
        if (!weekMonths[xCoord]) {
           weekMonths[xCoord] = monthStr
        }
      } else if (weekMonths[xCoord] === undefined) {
        weekMonths[xCoord] = ''
      }

      chartData.push({
        x: xCoord,
        y: yCoord,
        value: val,
        date: currentDate.getTime(),
        custom: {
          formattedDate: dateStr,
          monthDay: currentDate.getUTCDate(),
        },
      })
    }

    const textColor = isDark ? '#e4e4e7' : '#27272a' // zinc-200 / zinc-800
    const nullColor = isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'

    return {
      chart: {
        type: 'heatmap',
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
      },
      title: {
        text: '',
      },
      accessibility: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
        formatter: function () {
          const point = (this as any).point || this
          if (point.value === 0) {
            return `<b>${point.custom.formattedDate}</b><br/>${t('analytics.no_expenses', 'No expenses')}`
          }
          return `<b>${point.custom.formattedDate}</b><br/>${point.value.toFixed(2)} PLN`
        },
      },
      plotOptions: {
        heatmap: {
          point: {
            events: {
              click: function() {
                const point = this as any
                setSelectedDate(point.custom.formattedDate)
              },
            },
          },
        },
      },
      xAxis: {
        visible: true,
        categories: weekMonths,
        opposite: true, // Display at the top
        lineWidth: 0,
        tickWidth: 0,
        labels: {
          style: {
            color: textColor,
            fontSize: '11px',
            textTransform: 'capitalize',
          },
        },
      },
      yAxis: {
        categories: WEEKDAYS,
        title: undefined,
        reversed: true, // Display Sun-Sat top-to-bottom
        labels: {
          style: {
            color: textColor,
          },
        },
        gridLineColor: 'transparent',
      },
      colorAxis: {
        min: 0,
        max: maxVal > 0 ? maxVal : 100, // Fallback max
        stops: [
          [0, nullColor],
          [0.01, isDark ? '#064e3b' : '#d1fae5'], // emerald-900/100
          [0.5, isDark ? '#10b981' : '#10b981'],  // emerald-500
          [1, isDark ? '#34d399' : '#047857'],    // emerald-400/700
        ],
        labels: {
          style: { color: textColor },
          format: '{value}',
        },
      },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        margin: 20,
        symbolWidth: 300,
        itemStyle: {
          color: textColor,
          fontWeight: 'normal',
        },
      },
      series: [
        {
          name: 'Expenses',
          cursor: 'pointer',
          borderWidth: 2,
          borderColor: isDark ? '#18181b' : '#ffffff', // matches card background
          data: chartData,
          dataLabels: {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            format: '{point.custom.monthDay}',
            style: {
              textOutline: 'none',
              color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
              fontSize: '10px',
              fontWeight: 'bold',
            },
            x: 2,
            y: 2,
          },
          states: {
            hover: {
              color: isDark ? '#10b981' : '#34d399',
            },
            select: {
              color: '#3b82f6', // highlight selected day with blue
            },
          },
          allowPointSelect: true,
        } as any,
      ],
      credits: {
        enabled: false,
      },
    }
  }, [reportItems, filters, isDark, t, setSelectedDate, WEEKDAYS])

  return (
    <div className="w-full overflow-x-auto overflow-y-hidden pb-4">
      <div>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </div>
    </div>
  )
}
